import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Check, ChevronRight, Eraser, FileText } from "lucide-react";
import { toast } from "sonner";
import { LEGAL, UPDATED } from "@/lib/content";
import { useSeo } from "@/lib/seo";
import { deviceStorageStatus, resetDevice } from "@/lib/visitor";

/** Cookies page only: one-click erase of everything LUX keeps on this device. */
function DeviceStorageCard() {
  const [items, setItems] = useState(deviceStorageStatus);
  const [done, setDone] = useState(false);
  const stored = items.filter((i) => i.present).length;
  const reset = () => {
    const n = resetDevice();
    setItems(deviceStorageStatus());
    setDone(true);
    toast.success(n ? `Cleared ${n} item${n === 1 ? "" : "s"} from this device` : "Nothing was stored on this device", {
      description: "The 18+ confirmation will be asked again when you leave this page.",
    });
  };
  return (
    <section className="lux-card mt-12 p-6 sm:p-8" aria-labelledby="device-storage-h" data-testid="device-storage-card">
      <p className="lux-label">Stored on this device</p>
      <h2 id="device-storage-h" className="mt-3 font-serif text-3xl text-lux-text">Reset your preferences</h2>
      <p className="mt-3 max-w-2xl text-sm text-lux-muted">
        Everything below lives only in your browser. Erasing it clears your 18+ confirmation, photo-blur setting, saved shortlist and the random
        device identifier. LUX keeps no copy, so there is nothing to delete on our side.
      </p>
      <ul className="mt-5 grid gap-2 sm:grid-cols-2" data-testid="device-storage-list">
        {items.map((i) => (
          <li key={i.key} className="flex items-center justify-between rounded-[10px] border border-lux-border bg-lux-surface2 px-3.5 py-2.5 text-sm" data-testid={`device-storage-item-${i.key}`}>
            <span className="text-lux-text/90">{i.label} <code className="ml-1 text-xs text-lux-muted">{i.key}</code></span>
            <span className={`text-xs ${i.present ? "text-lux-silverBright" : "text-lux-muted"}`} data-testid={`device-storage-state-${i.key}`}>{i.present ? "stored" : "empty"}</span>
          </li>
        ))}
      </ul>
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button onClick={reset} className="lux-btn lux-btn-primary" data-testid="device-storage-reset-button">
          <Eraser className="h-4 w-4" aria-hidden="true" /> Erase everything stored on this device
        </button>
        {done && <span className="inline-flex items-center gap-1.5 text-sm text-lux-silver" role="status" data-testid="device-storage-reset-done"><Check className="h-4 w-4" aria-hidden="true" /> Cleared{stored ? "" : " - nothing stored now"}</span>}
      </div>
    </section>
  );
}

const NAV = [
  ["terms", "Terms"],
  ["privacy", "Privacy"],
  ["cookies", "Cookies"],
  ["online-safety", "Online Safety Act"],
  ["complaints", "Complaints"],
  ["posting-guidelines", "Posting guidelines"],
];

export default function LegalPage({ slug }) {
  const location = useLocation();
  const key = slug || location.pathname.replace(/^\//, "");
  const data = LEGAL[key] || LEGAL.terms;
  useSeo({
    title: `${data.title} | LUX`,
    description: data.intro,
  });
  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8" data-testid={`legal-page-${key}`}>
      <section className="pb-6 pt-12 sm:pt-16">
        <nav className="text-xs text-lux-muted" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-lux-silverBright">Home</Link>
          <ChevronRight className="mx-1 inline h-3 w-3 text-lux-silver" aria-hidden="true" />
          <span className="text-lux-text">{data.title}</span>
        </nav>
        <p className="lux-label mt-6">Legal</p>
        <h1 className="mt-3 font-serif text-5xl leading-[1.02] sm:text-6xl" data-testid="legal-title">
          {data.title}
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-lux-text/80">{data.intro}</p>
        <p className="mt-4 inline-flex items-center gap-2 text-xs text-lux-muted">
          <FileText className="h-3.5 w-3.5 text-lux-silver" aria-hidden="true" /> {UPDATED}
        </p>
      </section>

      <div className="grid gap-10 lg:grid-cols-[220px_1fr]">
        <aside className="h-fit lg:sticky lg:top-24">
          <p className="lux-label mb-3">Contents</p>
          <ul className="flex flex-col gap-1 border-l border-lux-border pl-4 lg:border-l lg:border-lux-border">
            {NAV.map(([k, label]) => (
              <li key={k}>
                <Link
                  to={`/${k}`}
                  className={`-ml-[1px] block border-l-2 py-1.5 pl-3 text-sm transition-colors ${
                    key === k
                      ? "border-lux-silverBright text-lux-silverBright"
                      : "border-transparent text-lux-muted hover:border-lux-silver/60 hover:text-lux-text"
                  }`}
                  data-testid={`legal-nav-${k}`}
                >
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </aside>

        <article className="lux-prose max-w-[72ch]" data-testid="legal-content">
          {data.sections.map((section) => (
            <section key={section.h} className="mb-10">
              <h2>{section.h}</h2>
              {(section.p || []).map((p, i) => (
                <p key={i}>{p}</p>
              ))}
              {section.list && (
                <ul>
                  {section.list.map((li, i) => (
                    <li key={i}>{li}</li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </article>
      </div>

      {key === "cookies" && <DeviceStorageCard />}

      <section className="mt-16 lux-card p-6 sm:p-8" data-testid="legal-contact">
        <p className="lux-label">Need to reach us?</p>
        <h2 className="mt-3 font-serif text-3xl text-lux-text">Report, appeal or ask a question</h2>
        <p className="mt-3 max-w-2xl text-sm text-lux-muted">
          Visitors can report a profile or send a complaint through the <Link to="/report" className="lux-link">report page</Link>. Advertisers can submit a support ticket or appeal from inside the dashboard once signed in.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/report" className="lux-btn lux-btn-primary" data-testid="legal-report-cta">Report a concern</Link>
          <Link to="/help" className="lux-btn lux-btn-secondary" data-testid="legal-help-cta">Help &amp; FAQ</Link>
        </div>
      </section>
    </div>
  );
}
